import PropTypes from 'prop-types';
import { useMemo, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { useEventCallback } from '@icp/hooks';
import { createPortal } from 'react-dom';

const SIZE = 50;
const EDGE_DISTANCE = 10; // 吸边距离
const FAULT_TOLERANCE_DISTANCE = 6; // 点击容错距离

const minmax = (min, value, max) => Math.min(Math.max(min, value), max);

function FloatButton(props) {
  const { className, style, tooltip, icon, onClick: onClickProp } = props;

  const ref = useRef();
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState({
    left: window.innerWidth - SIZE - EDGE_DISTANCE,
    top: window.innerHeight - SIZE - EDGE_DISTANCE,
  });
  const onClick = useEventCallback(onClickProp);

  const handlers = useMemo(() => {
    // 通用的拖拽处理函数
    const handleDragStart = (clientX, clientY) => {
      setDragging(true);

      const { left: initialX, top: initialY } = ref.current.getBoundingClientRect();
      let currentX = initialX;
      let currentY = initialY;
      // 记录点击位置在按钮内部的偏移
      const clickOffset = {
        dx: clientX - initialX,
        dy: clientY - initialY,
      };

      const handleDragMove = (moveClientX, moveClientY) => {
        // 直接根据鼠标位置和偏移量计算按钮应处的位置
        const newX = moveClientX - clickOffset.dx;
        const newY = moveClientY - clickOffset.dy;
        // 限制按钮不能拖出屏幕边界
        currentX = Math.max(0, Math.min(window.innerWidth - SIZE, newX));
        currentY = Math.max(0, Math.min(window.innerHeight - SIZE, newY));
        setPosition({
          left: currentX,
          top: currentY,
        });
      };

      const handleDragEnd = () => {
        setDragging(false);
        // 移动位置足够小时视为用户点击操作
        if (
          currentX <= initialX + FAULT_TOLERANCE_DISTANCE &&
          currentX >= initialX - FAULT_TOLERANCE_DISTANCE &&
          currentY <= initialY + FAULT_TOLERANCE_DISTANCE &&
          currentY >= initialY - FAULT_TOLERANCE_DISTANCE
        ) {
          onClick();
        }

        // 松手吸边处理
        const finalRect = ref.current.getBoundingClientRect();
        const distance = {
          toTop: finalRect.top,
          toBottom: window.innerHeight - finalRect.top - SIZE,
          toLeft: finalRect.left,
          toRight: window.innerWidth - finalRect.left - SIZE,
        };
        const minDistance = Math.min(
          distance.toTop,
          distance.toBottom,
          distance.toLeft,
          distance.toRight,
        );
        const minTop = EDGE_DISTANCE;
        const maxTop = window.innerHeight - SIZE - EDGE_DISTANCE;
        const minLeft = EDGE_DISTANCE;
        const maxLeft = window.innerWidth - SIZE - EDGE_DISTANCE;
        switch (minDistance) {
          case distance.toLeft:
            setPosition((prev) => ({
              left: minLeft,
              top: minmax(minTop, prev.top, maxTop),
            }));
            break;
          case distance.toRight:
            setPosition((prev) => ({
              left: maxLeft,
              top: minmax(minTop, prev.top, maxTop),
            }));
            break;
          case distance.toTop:
            setPosition((prev) => ({
              top: minTop,
              left: minmax(minLeft, prev.left, maxLeft),
            }));
            break;
          case distance.toBottom:
            setPosition((prev) => ({
              top: maxTop,
              left: minmax(minLeft, prev.left, maxLeft),
            }));
            break;
          default:
            break;
        }
      };

      return { handleDragMove, handleDragEnd };
    };

    // 鼠标事件处理器
    const onMouseDown = (downEvent) => {
      downEvent.preventDefault();
      downEvent.stopPropagation();

      const { handleDragMove, handleDragEnd } = handleDragStart(
        downEvent.clientX,
        downEvent.clientY,
      );

      const onMouseMove = (moveEvent) => {
        moveEvent.preventDefault();
        moveEvent.stopPropagation();
        handleDragMove(moveEvent.clientX, moveEvent.clientY);
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        handleDragEnd();
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    // 触摸事件处理器
    const onTouchStart = (downEvent) => {
      downEvent.preventDefault();
      downEvent.stopPropagation();

      const touch = downEvent.touches[0];
      const { handleDragMove, handleDragEnd } = handleDragStart(touch.clientX, touch.clientY);

      const onTouchMove = (moveEvent) => {
        moveEvent.preventDefault();
        moveEvent.stopPropagation();
        const moveTouch = moveEvent.touches[0];
        if (moveTouch) {
          handleDragMove(moveTouch.clientX, moveTouch.clientY);
        }
      };

      const onTouchEnd = () => {
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('touchend', onTouchEnd);
        handleDragEnd();
      };

      document.addEventListener('touchmove', onTouchMove, { passive: false });
      document.addEventListener('touchend', onTouchEnd);
    };

    return { onMouseDown, onTouchStart };
  }, [onClick]);

  return createPortal(
    <Tooltip title={tooltip}>
      <div
        ref={ref}
        className={className}
        style={{
          ...style,
          ...position,
          width: SIZE,
          height: SIZE,
          position: 'fixed',
          zIndex: 1,
          cursor: dragging ? 'grabbing' : 'pointer',
          ...(!dragging && { transition: 'all 0.2s ease-in-out' }),
        }}
        onMouseDown={handlers.onMouseDown}
        onTouchStart={handlers.onTouchStart}
      >
        {icon}
      </div>
    </Tooltip>,
    document.body,
  );
}

FloatButton.propTypes = {
  className: PropTypes.string,
  style: PropTypes.shape({}),
  tooltip: PropTypes.string,
  icon: PropTypes.node,
  onClick: PropTypes.func,
};

export default FloatButton;
